import { useRef } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";

// Same OAuth client used by GoogleOAuthProvider in index.tsx. Native Google
// Sign-In (Android Credential Manager) needs this passed explicitly as
// `webClientId` even though the app never runs as a web client there — see
// https://github.com/Cap-go/capacitor-social-login#android-configuration.
const GOOGLE_WEB_CLIENT_ID =
  "858808532426-85clcmvdjil1kges81eaa7udk4sogqlf.apps.googleusercontent.com";

const isNative = Capacitor.isNativePlatform();

// @react-oauth/google's popup flow doesn't work in a Capacitor WebView: the
// popup opens as a separate Android browser Task with no `window.opener` for
// Google's redirect to post back to, so it hangs on a blank page after
// account selection. Native builds use the platform's Credential Manager
// (via @capgo/capacitor-social-login) instead, which returns a real OAuth
// access token compatible with the existing google-login/google-register
// endpoints.
let nativeGoogleAuthReady: Promise<void> | null = null;
function initializeNativeGoogleAuth() {
  if (!nativeGoogleAuthReady) {
    nativeGoogleAuthReady = SocialLogin.initialize({
      google: { webClientId: GOOGLE_WEB_CLIENT_ID },
    });
  }
  return nativeGoogleAuthReady;
}

interface UseGoogleAuthOptions {
  onAccessToken: (accessToken: string) => void | Promise<void>;
  onError?: () => void;
}

/**
 * Returns a function that triggers Google sign-in, using the native
 * Credential Manager flow on Capacitor and the browser popup flow on web.
 * Both paths resolve to a Google OAuth access_token passed to onAccessToken.
 */
export function useGoogleAuth({ onAccessToken, onError }: UseGoogleAuthOptions) {
  // Bound to the in-flight login attempt and checked against the value Google
  // echoes back, so a stray/replayed callback can't be accepted as this one
  // (Google flags OAuth clients that omit `state` as CSRF-vulnerable).
  const pendingStateRef = useRef<string | null>(null);

  const webGoogleLogin = useGoogleLogin({
    scope: "email profile",
    onSuccess: async (tokenResponse) => {
      const expectedState = pendingStateRef.current;
      pendingStateRef.current = null;
      if (!expectedState || tokenResponse.state !== expectedState) {
        onError?.();
        return;
      }
      await onAccessToken(tokenResponse.access_token);
    },
    onError: () => {
      pendingStateRef.current = null;
      onError?.();
    },
  });

  const startWebGoogleLogin = () => {
    const state = crypto.randomUUID();
    pendingStateRef.current = state;
    webGoogleLogin({ state });
  };

  const nativeGoogleLogin = async () => {
    try {
      await initializeNativeGoogleAuth();
      const { result } = await SocialLogin.login({
        provider: "google",
        options: { scopes: ["email", "profile"] },
      });
      if (result.responseType !== "online" || !result.accessToken?.token) {
        throw new Error("Google sign-in did not return an access token");
      }
      await onAccessToken(result.accessToken.token);
    } catch (err) {
      if ((err as { code?: string } | undefined)?.code === "USER_CANCELLED") return;
      console.error(err);
      onError?.();
    }
  };

  return () => {
    if (isNative) {
      void nativeGoogleLogin();
    } else {
      startWebGoogleLogin();
    }
  };
}

/**
 * Clears native Credential Manager state on logout. `googleLogout()` from
 * @react-oauth/google (used elsewhere in the logout flow) only clears web
 * session state and does nothing on native, so without this the native
 * Google session stays signed in across app logout.
 */
export async function signOutOfNativeGoogle() {
  if (!isNative) return;
  try {
    await SocialLogin.logout({ provider: "google" });
  } catch (err) {
    console.error(err);
  }
}
